import { Compass, Hammer, Megaphone, TrendingUp, ArrowRight, Rocket, Store, ShoppingCart, LineChart, Layers, Users, ShieldCheck, Handshake } from "lucide-react";
import { ACCENT, Eyebrow, H2, CtaLink, MarketplaceLogos } from "@/components/ecommerce/shared";

const STAGES = [
  { key: "plan", Icon: Compass, name: "Plan", items: ["eCommerce strategy", "Customer understanding", "Product positioning", "Pricing and channel selection"] },
  { key: "build", Icon: Hammer, name: "Build", items: ["Online store development", "Product catalogues", "Payments and checkout", "Delivery and fulfilment configuration"] },
  { key: "sell", Icon: Megaphone, name: "Sell", items: ["Marketplace enablement", "Customer acquisition", "Landing pages and campaigns", "Conversion optimisation"] },
  { key: "grow", Icon: TrendingUp, name: "Grow", items: ["Customer journeys", "CRM and follow-up", "Customer support automation", "Analytics and ongoing improvement"] },
];

export function EcomCapabilities() {
  return (
    <section id="capabilities" aria-labelledby="capabilities-heading" data-testid="ecom-capabilities-section" className="relative py-20 sm:py-28 bg-[#16120F] text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow dark>eCommerce enablement services</Eyebrow>
          <H2 id="capabilities-heading" dark>Every Part of Your eCommerce System, Connected</H2>
          <p className="mt-5 text-white/75 text-base sm:text-lg leading-relaxed">
            Selling online depends on more than a storefront. Rekisa groups the capabilities behind an online sale into one practical
            operating model — so strategy, store, marketplaces and growth activities support each other instead of running separately.
          </p>
        </div>
        <ol data-testid="ecom-capabilities-diagram" aria-label="Plan, Build, Sell, Grow — the connected eCommerce system" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STAGES.map((s, i) => (
            <li key={s.key} data-testid={`ecom-capability-${s.key}`} className="relative rounded-3xl border border-white/10 bg-white/[0.04] p-6">
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: ACCENT }}><s.Icon className="h-5 w-5 text-white" /></span>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-white/50">Stage {i + 1}</p>
                  <h3 className="font-display text-2xl font-black tracking-tight">{s.name}</h3>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-white/80"><span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: ACCENT }} />{it}</li>
                ))}
              </ul>
              {i < STAGES.length - 1 && (
                <span aria-hidden className="hidden lg:flex absolute top-9 -right-[18px] z-10 h-8 w-8 rounded-full bg-white text-zinc-900 items-center justify-center shadow">
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </li>
          ))}
        </ol>
        <p data-testid="ecom-capabilities-note" className="mt-8 text-sm text-white/60 max-w-3xl leading-relaxed">
          Unlike a supplier that only creates websites, Rekisa understands how these components work together. Not every capability is
          included in every engagement — your proposal sets out the agreed scope.
        </p>
      </div>
    </section>
  );
}

const STEPS = [
  ["Understand", "We assess your business, products, customers, goals and current selling channels."],
  ["Plan", "We recommend the eCommerce strategy, platforms, marketplace approach and implementation priorities."],
  ["Build", "We configure the agreed online store, product catalogues, marketplace components and supporting workflows."],
  ["Launch", "We test relevant customer journeys, checkout, payments, delivery and operational processes before going live."],
  ["Improve", "We help identify opportunities to improve conversions, customer acquisition and ongoing operations within the agreed service scope."],
];

export function EcomHowItWorks() {
  return (
    <section id="how-it-works" aria-labelledby="how-it-works-heading" data-testid="ecom-how-it-works-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>eCommerce implementation · store launch process</Eyebrow>
          <H2 id="how-it-works-heading">From Your eCommerce Idea to a Working Online Sales System</H2>
        </div>
        <ol className="mt-12 grid md:grid-cols-5 gap-4">
          {STEPS.map(([t, c], i) => (
            <li key={t} data-testid={`ecom-step-${i + 1}`} className="rounded-3xl border border-zinc-200 bg-[#FBF7F2] p-6">
              <span className="font-display text-4xl font-black" style={{ color: ACCENT }}>{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 font-display text-xl font-black text-zinc-900">{t}</h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{c}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10"><CtaLink label="Let's Build My eCommerce Plan" location="ecom_how_it_works" testid="ecom-how-it-works-cta" /></div>
      </div>
    </section>
  );
}

const SEGMENTS = [
  { key: "starting", Icon: Rocket, name: "Starting Online", who: "Businesses preparing to create their first online sales channel.",
    help: "We help you choose the right channel — your own store, a marketplace or both — and plan the products, pricing and setup needed to start." },
  { key: "launching", Icon: Store, name: "Launching an Online Store", who: "Businesses that have products and need a professional eCommerce storefront.",
    help: "We build the store around your catalogue, with checkout, supported payments, delivery configuration and customer notifications." },
  { key: "marketplaces", Icon: ShoppingCart, name: "Entering Marketplaces", who: "Brands and retailers preparing to sell through Takealot, Amazon or Makro.",
    help: "We assess readiness, support onboarding steps and prepare catalogues and listings that follow each marketplace's requirements." },
  { key: "growing", Icon: LineChart, name: "Growing an Existing Business", who: "Online sellers who want to improve their website, conversion, customer acquisition or sales operations.",
    help: "We review your customer journey, product pages and checkout, then prioritise practical improvements to acquisition, conversion and repeat sales." },
];

export function EcomWhoWeHelp() {
  return (
    <section id="who-we-help" aria-labelledby="who-we-help-heading" data-testid="ecom-who-we-help-section" className="relative py-20 sm:py-28 bg-[#FBF7F2] border-y border-zinc-200">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>eCommerce solutions for SMEs · South African retailers</Eyebrow>
          <H2 id="who-we-help-heading">eCommerce Solutions for Businesses at Different Stages</H2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {SEGMENTS.map((s) => (
            <article key={s.key} data-testid={`ecom-segment-${s.key}`} className="rounded-3xl border border-zinc-200 bg-white p-7">
              <span className="h-11 w-11 rounded-2xl flex items-center justify-center bg-[#16120F]"><s.Icon className="h-5 w-5 text-white" /></span>
              <h3 className="mt-5 font-display text-2xl font-black tracking-tight text-zinc-900">{s.name}</h3>
              <p className="mt-2 text-zinc-800 font-medium leading-relaxed">{s.who}</p>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed"><span className="font-semibold text-zinc-900">How Rekisa helps: </span>{s.help}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const TRUST = [
  { Icon: Users, title: "5,000+ South African SMEs empowered", copy: "Rekisa has empowered over 5,000 South African small and medium businesses with eCommerce solutions since 2020." },
  { Icon: ShoppingCart, title: "Marketplace seller enablement", copy: "We help sellers prepare their products, catalogues and operations for marketplaces including Amazon, Takealot and Makro." },
  { Icon: Layers, title: "Real-world implementation expertise", copy: "Online storefronts, payments and checkout, delivery configuration, product catalogues, marketplace operations, growth marketing and customer follow-up." },
  { Icon: Handshake, title: "One partner across the journey", copy: "Strategy, implementation, growth marketing, marketplace enablement and customer experience — connected by one team." },
  { Icon: ShieldCheck, title: "Honest, scoped commitments", copy: "Clear proposals that separate Rekisa's fees from platform, payment and marketplace costs. No guaranteed sales or approval claims." },
];

export function EcomWhyRekisa() {
  return (
    <section id="why-rekisa" aria-labelledby="why-rekisa-heading" data-testid="ecom-why-rekisa-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-start">
        <div>
          <Eyebrow>eCommerce services company</Eyebrow>
          <H2 id="why-rekisa-heading">One eCommerce Partner. The Expertise to Connect It All.</H2>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            Rekisa brings together eCommerce implementation, business strategy, growth marketing, marketplace enablement and customer experience.
          </p>
          <p className="mt-3 text-zinc-600 leading-relaxed">Since 2020, we've empowered more than 5,000 South African SMEs with eCommerce solutions.</p>
          <p className="mt-3 text-zinc-600 leading-relaxed">
            We understand that creating an online selling channel is only one part of building a successful digital business. The products,
            customer journey, payments, fulfilment, marketing and support processes also matter.
          </p>
          <div className="mt-8 rounded-3xl bg-[#FBF7F2] border border-zinc-200 p-6"><MarketplaceLogos testid="ecom-logos-why" compact /></div>
        </div>
        <ul className="grid gap-4">
          {TRUST.map((t, i) => (
            <li key={t.title} data-testid={`ecom-trust-${i}`} className="flex gap-4 rounded-2xl border border-zinc-200 p-5">
              <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${ACCENT}18`, color: ACCENT }}><t.Icon className="h-5 w-5" /></span>
              <div>
                <h3 className="font-display text-lg font-black text-zinc-900">{t.title}</h3>
                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{t.copy}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
